<template>
  <div>

    <div v-if="isLoading" class="loading-overlay">
      <div class="spinner-container">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
        <p class="loading-text">Please wait, converting PDF to flipbook...</p>
      </div>
    </div>
    <b-card title="Nota Dinas">
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label>Show</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block ml-50 mr-1"
            />
            <b-button variant="primary" @click="updateDocWindow()">
              Create Flip Book
            </b-button>
          </b-col>
  
          <!-- Search -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-end mb-1 mb-md-0"
          >
            <label class="mr-1">
              Search
            </label>
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                class="d-inline-block mr-1"
                placeholder="Search..."
                v-model="searchWord"
              />
            </div>
          </b-col>
        </b-row>
      </div>
      <!-- Larger screen than mobile -->
      <div class="d-none d-md-flex">
        <!-- Table -->
        <b-table responsive="sm" class="w-100" :items="dataList" :fields="fields" :busy="isBusy" @sort-changed="onSortChanged" @row-clicked="selectRow" :tbody-tr-class="rowClass">
          <template #table-busy>
            <div class="text-center text-danger my-2">
              <b-spinner class="align-middle mr-1" variant="secondary"></b-spinner>
              <strong class="text-secondary">Loading...</strong>
            </div>
          </template>
          <template #cell(action)="dataList">
          <div class="d-flex justify-content-start">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="secondary"
              class="btn-icon"
              @click="updateWindow(dataList)"
            >
              <feather-icon icon="Edit3Icon" />
            </b-button>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="danger"
              class="btn-icon ml-1"
              @click="deleteFlipBook(dataList.item.category)"
            >
              <feather-icon icon="Trash2Icon" />
            </b-button> 
          </div>
        </template>
        </b-table>
      </div>
      <!-- Mobile screen -->
      <div class="d-flex d-md-none flex-column">
        <!-- Loading -->
        <div v-if="isBusy" class="text-center text-danger my-2">
          <b-spinner class="align-middle mr-1" variant="secondary"></b-spinner>
          <strong class="text-secondary">Loading...</strong>
        </div>
        <!-- Data -->
        <div v-if="!isBusy && dataList.length == 0" class="text-center text-danger my-2">
          <strong class="text-secondary">No data</strong>
        </div>
        <b-row
          v-if="!isBusy && dataList.length > 0"
          v-for="(item, index) in dataList"
          :key="item.id"
          class="mb-3 list-item"
        >
          <b-col cols="12" class="d-flex flex-wrap justify-content-end align-items-center mb-1" style="gap: 1rem;">
            <strong class="flex-grow-1 truncate">{{ item.category }}</strong>
            <div>
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="secondary"
                class="btn-icon"
                @click="updateWindow({ item })"
              >
                <feather-icon icon="Edit3Icon" />
              </b-button>
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="danger"
                class="btn-icon ml-1"
                @click="deleteFlipBook(item.category)"
              >
                <feather-icon icon="Trash2Icon" />
              </b-button>
            </div>
          </b-col>
          <b-col cols="12" class="d-flex flex-wrap">
            <div style="font-size: 0.75rem;">{{ item.folders }}</div>
          </b-col>
        </b-row>
      </div>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">
              Showing {{ pagination.from }} to {{ pagination.to }} of
              {{ pagination.total }} entries
            </span>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="pagination.total"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
              @input="changePage"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
        <b-modal
          id="modal-create-flipbook"
          cancel-variant="outline-secondary"
          ok-title="Ok"
          cancel-title="Close"
          centered
          title="Create Flipbook"
          ref="modalCreateFlipBook"
          no-close-on-backdrop
          
          no-close-on-esc
          @ok="
            $event.preventDefault()
            createFlipBook()
          "
        >
          <b-form>
            <b-row>
              <b-col sm="12">
                <b-form-group label="Category *" label-for="category"> 
                  <b-form-select v-model="form.category" :options="productCategories"></b-form-select>
                </b-form-group>
              </b-col>
              <b-col sm="12" class="mb-3">
                <b-form-group label="Upload PDF" label-for="file">
                  <b-form-file
                    v-model="form.file"
                    :state="Boolean(form.file)"
                    placeholder="Drag and drop your file here or click to select"
                    accept=".pdf"
                    ref="file-input"
                    @dragover.prevent
                    @drop.prevent="handleFileUpload"
                    @change="handleFileUpload"
                  ></b-form-file>
                </b-form-group>
              </b-col>
            </b-row>
          </b-form>
          <template #modal-ok>
              Save
          </template>
        </b-modal>
        <b-modal
          id="modal-update-flipbook"
          cancel-variant="outline-secondary"
          ok-title="Ok"
          cancel-title="Close"
          centered
          title="Update Flipbook"
          ref="modalUpdateFlipBook"
          no-close-on-backdrop
          
          no-close-on-esc
          @ok="
            $event.preventDefault()
            updateFlipBook()
          "
        >
          <b-form>
            <b-row>
              <b-col sm="12" class="mb-3">
                <b-form-group label="Upload PDF" label-for="file">
                  <b-form-file
                    v-model="form.file"
                    :state="Boolean(form.file)"
                    placeholder="Drag and drop your file here or click to select"
                    accept=".pdf"
                    ref="file-input"
                    @dragover.prevent
                    @drop.prevent="handleFileUpload"
                    @change="handleFileUpload"
                  ></b-form-file>
                </b-form-group>
              </b-col>
            </b-row>
          </b-form>
          <template #modal-ok>
            Save
          </template>
        </b-modal>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardText,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BTooltip,
  BSpinner,
  BFormCheckbox,
  BModal,
  BImg,
  BFormFile,
  BFormGroup,
  BForm,
  BAlert,
  BFormSelect,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import { generateToken } from '@/auth/helperAuth'

export default {
  components: {
    BCard,
    BCardText,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BFormGroup,
    BForm,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BTooltip,
    BSpinner,
    BFormCheckbox,
    BModal,
    BImg,
    BFormFile,
    vSelect,
    BAlert,
    BFormSelect
  },
  data() {
    return {
      token: '',
      isBusy: false,
      currentPage: 1,
      perPage: 10,
      perPageOptions: [10, 20, 30],
      pagination: {
        from: 1,
        to: 10,
        total: 0,
      },
      fields: [
        { key: 'category', sortable: true },
        { key: 'folders', label:'Flip Book', sortable: true},
        'action',
        // { key: 'createdDate', sortable: true},
      ],
      dataList: [],
      searchWord: '',
      typeUpdate: '',
      form: {
        category: '',
        file: null,
      },
      loadingUpdateFile: false,
      sortField : '',
      orderBy: '',
      selectedRows: [],
      show:false,
      statusDownload: '',
      downloadPath: '',
      isChecked:false,
      loadDelete: false,
      loadDownload: false,
      isLoading:false,
      productCategories: [],
      category: ''
    }
  },

  directives: {
    Ripple,
  },

  created() {
    const payload = {
      client: "product-catalog-fe",
      kode: this.$store.getters['oauth/getActiveUser'].id.toString(),
      user: this.$store.getters['oauth/getActiveUser'].name,
      role: this.$store.getters['oauth/getActiveUser'].role,
    };
    this.token = generateToken(payload)
  },

  filters: {
    truncate: function(value, length, start = 0) {
      if (!value) return ''
      value = value.toString()
      if (value.length > length) {
        if (start > 0) {
          return value.substring(start, length + start) + '...'
        } else {
          return value.substring(start, length) + '...'
        }
      } else {
        return value
      }
    },
  },

  mounted() {
    this.fetchData()
    Promise.all([
      this.getProductCategory()
    ])
  },

  methods: {
    updateWindow(data) {
      this.category = data.item.category
      this.$refs.modalUpdateFlipBook.show()
    },
    fetchData() {
      this.isBusy = true
      this.$http
        .get('https://amalsolution-dev.com:9192/list/categories/folders', {
          withCredentials: false,
          headers: {'Client-Device': 'browser', 'Authorization': `${this.token}`}
        })
        .then(response => {
          console.log(response)
          this.dataList = response.data.map(item => ({
          ...item,
          }))
        })
        .finally(() => {
          this.isBusy = false
        })
    },
    async getProductCategory() {
      return await this.$http.get(`/product-category/take/all`)
      .then( res => {
        for(let i of res.data.data.records){
          this.productCategories.push({
            text: i.name,
            value: i.name
          })
        }
      })
      .catch( err => {
        console.error(err)
      })
    },
    updateDocWindow() {
      this.$refs.modalCreateFlipBook.show()
    },
    updateFlipBook() {
      this.$bvModal
        .msgBoxConfirm('This process will take approximately 10-30 minutes, depending on the file size and the number of pages.', {
          title: 'Please Confirm',
          size: 'sm',
          okVariant: 'danger',
          okTitle: 'Yes',
          cancelTitle: 'No',
          cancelVariant: 'outline-secondary',
          hideHeaderClose: false,
          centered: true,
        })
        .then(value => {
          if (value === true) {
            this.$refs.modalUpdateFlipBook.hide()
            this.isLoading=true
            const formData = new FormData()
            formData.append('pdf', this.form.file)
            this.$http
              .put(`https://amalsolution-dev.com:9192/flipbook/${this.category}`, formData, {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
                withCredentials: false,
                headers:{'Client-Device': 'browser', 'Authorization': `${this.token}`}
              })
              .then(res => {
                this.$notif('Success', 'Successfully Update Flip Book', 'success')
                this.$router.push('/admin/flip/book')
              })
              .catch(err => {
                if (typeof err.data.errors !== 'undefined') {
                  this.$notif('Error', err.data.errors.file[0], 'error')
                }else{
                  this.errString = err.data.replace(",","<br>")
                  this.$notif('Error', `Something wrong`, 'error')
                }
              })
              .finally(() => (this.isLoading = false))
          }
        })
    },
    createFlipBook() {
      this.$bvModal
        .msgBoxConfirm('This process will take approximately 10-30 minutes, depending on the file size and the number of pages.', {
          title: 'Please Confirm',
          size: 'sm',
          okVariant: 'danger',
          okTitle: 'Yes',
          cancelTitle: 'No',
          cancelVariant: 'outline-secondary',
          hideHeaderClose: false,
          centered: true,
        })
        .then(value => {
          if (value === true) {
            this.$refs.modalCreateFlipBook.hide()
            this.isLoading=true
            const formData = new FormData()
            formData.append('pdf', this.form.file)
            formData.append('category', this.form.category)
            this.$http
              .post('https://amalsolution-dev.com:9192/flipbook', formData, {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
                withCredentials: false,
                headers:{'Client-Device': 'browser', 'Authorization': `${this.token}`}
              })
              .then(res => {
                this.$notif('Success', 'Successfully Create Flip Book', 'success')
                this.$router.push('/admin/flip/book')
              })
              .catch(err => {
                if (typeof err.data.errors !== 'undefined') {
                  this.$notif('Error', err.data.errors.file[0], 'error')
                }else{
                  this.errString = err.data.replace(",","<br>")
                  this.$notif('Error', `Something wrong`, 'error')
                }
              })
              .finally(() => (this.isLoading = false))
          }
        })
    },
    deleteFlipBook(category) {
      this.category = category
      this.$bvModal
        .msgBoxConfirm('This process will take approximately 1-10 minutes, depending on the file size and the number of pages.', {
          title: 'Please Confirm',
          size: 'sm',
          okVariant: 'danger',
          okTitle: 'Yes',
          cancelTitle: 'No',
          cancelVariant: 'outline-secondary',
          hideHeaderClose: false,
          centered: true,
        })
        .then(value => {
          if (value === true) {
            this.isLoading=true
            this.$http
              .delete(`https://amalsolution-dev.com:9192/flipbook/${this.category}`, {
                headers: {},
                withCredentials: false,
                headers:{'Client-Device': 'browser', 'Authorization': `${this.token}`}
              })
              .then(res => {
                this.$notif('Success', 'Successfully For Deleting Flip Book', 'success')
              })
              .catch(err => {
                if (typeof err.data.errors !== 'undefined') {
                  this.$notif('Error', err.data.errors.file[0], 'error')
                }else{
                  this.errString = err.data.replace(",","<br>")
                  this.$notif('Error', `Something wrong`, 'error')
                }
              })
              .finally(() => (this.isLoading = false))
          }
        })
    },
    changePage(page) {
      this.currentPage = page
    },
    onSortChanged(ctx){
      if(ctx.sortBy == 'category'){
        this.sortField = 'pc.name'
      }else if(ctx.sortBy == 'type') {
        this.sortField = 'nd.type'
      }else if(ctx.sortBy == 'documentDate'){
        this.sortField = 'date'
      }else{
        this.sortField = ctx.sortBy
      }
      this.orderBy = ( (ctx.sortDesc == false ) ? 'ASC' : 'DESC')
    },
    handleFileUpload(event) {
      let files;
      if (event.dataTransfer) {
        files = event.dataTransfer.files
      } else if (event.target.files) {
        files = event.target.files
      }
      if (files && files.length) {
        this.form.file = files[0]
      }
    },
    selectRow(item, index) {
      const foundIndex = this.selectedRows.findIndex(row => row.id === item.id)
      if (foundIndex === -1) {
        this.selectedRows.push(item)
      } else {
        this.selectedRows.splice(foundIndex, 1)
      }
    },
    rowClass(item) {
      return this.selectedRows.some(row => row.id === item.id) ? 'table-row-selected' : 'pointer-row';
    }
  },
}
</script>
<style>
.modal-header {
  background-color: red !important;
}
.modal-title {
  color:white !important;
  font-weight: 600
}
.table-row-selected {
  background-color: #f7d5d5 !important;
  cursor: pointer;
}
.pointer-row{
  cursor: pointer;
}
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5); /* Black background with 50% opacity */
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999; /* Ensure it is on top of all elements */
}

/* Centered spinner container */
.spinner-container {
  text-align: center;
  color: #fff; /* White text for better visibility */
}

/* Loading text style */
.loading-text {
  margin-top: 1rem;
  font-size: 1.2rem;
}
</style>
<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
